import { z } from 'zod';

/**
 * Stable, machine-readable error codes. Clients switch on `code`; the `message`
 * is for people and may be reworded at any time without a breaking change.
 */
export const ERROR_CODES = [
  // 400
  'validation_failed',
  'malformed_request',
  'invalid_filter',
  'invalid_transition',
  'unsupported_operation',
  // 401 / 403
  'unauthenticated',
  'invalid_credentials',
  // A sign-in that was understood and refused — no account, or a deactivated
  // one. Separate from `invalid_credentials`, which means the secret was wrong:
  // these two want different words in front of the person, and conflating them
  // either tells an attacker an account exists or leaves a real user guessing.
  'sign_in_refused',
  'session_expired',
  'mfa_required',
  'mfa_invalid',
  'password_expired',
  'forbidden',
  'insufficient_permission',
  'out_of_scope',
  // 404 / 409 / 410
  'not_found',
  'conflict',
  'duplicate',
  'stale_write',
  'gone',
  // 413 / 415 / 422
  'payload_too_large',
  'unsupported_media_type',
  'unprocessable',
  // 429
  'rate_limited',
  // 500+
  'internal_error',
  'dependency_unavailable',
  'not_implemented',
] as const;

export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/** One field-level problem, addressable by a JSON pointer-ish path. */
export const FieldErrorSchema = z.object({
  /** Dotted path into the request body, e.g. `custom_fields.cost_centre`. */
  path: z.string(),
  message: z.string(),
  code: z.string().optional(),
});
export type FieldError = z.infer<typeof FieldErrorSchema>;

/**
 * The single error envelope every endpoint returns. Modelled on RFC 9457
 * (problem details) but with a flat, predictable shape.
 */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    /** Safe to display. Never contains stack traces, SQL, or internal hostnames. */
    message: z.string(),
    /** Present for `validation_failed`. */
    details: z.array(FieldErrorSchema).optional(),
    /** Correlates the response with server logs. Always present. */
    request_id: z.string(),
    /** Seconds to wait, on `rate_limited`. */
    retry_after: z.number().int().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const ERROR_STATUS: Record<ErrorCode, number> = {
  validation_failed: 422,
  malformed_request: 400,
  invalid_filter: 400,
  invalid_transition: 409,
  unsupported_operation: 400,
  unauthenticated: 401,
  invalid_credentials: 401,
  sign_in_refused: 403,
  session_expired: 401,
  mfa_required: 401,
  mfa_invalid: 401,
  password_expired: 401,
  forbidden: 403,
  insufficient_permission: 403,
  out_of_scope: 403,
  not_found: 404,
  conflict: 409,
  duplicate: 409,
  stale_write: 409,
  gone: 410,
  payload_too_large: 413,
  unsupported_media_type: 415,
  unprocessable: 422,
  rate_limited: 429,
  internal_error: 500,
  dependency_unavailable: 503,
  not_implemented: 501,
};
